import { MessageSquareMoreIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { EmptyState, ErrorState, LoadingState, Table } from '@/components/common';
import { ConversationRow } from '@/components/messaging/ConversationRow';
import { useCurrentUser } from '@/contexts';
import { useConversations } from '@/hooks/useConversations';
import { userService } from '@/services';
import type { UserProfile } from '@/types';
import { firstPhotoUrl } from '@/utils';

export function MessagesTabContent() {
  const { t } = useTranslation('chat');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { filteredConversations, isLoading, isError, refetch } = useConversations(user);
  const [partnerCache, setPartnerCache] = useState<Record<string, UserProfile>>({});

  useEffect(() => {
    if (!user || filteredConversations.length === 0) return;

    let cancelled = false;

    void (async () => {
      const ids = [
        ...new Set(
          filteredConversations
            .map(conv => conv.userUids.find(id => id !== user.uid))
            .filter((id): id is string => Boolean(id))
        ),
      ];

      const profiles = await Promise.all(
        ids.map(async id => [id, await userService.getPublicProfile(id)] as const)
      );
      if (cancelled) return;

      setPartnerCache(prev => {
        let changed = false;
        const next = { ...prev };
        for (const [id, profile] of profiles) {
          if (profile && !next[id]) {
            next[id] = profile;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [filteredConversations, user]);

  if (isLoading) return <LoadingState />;
  if (isError)
    return (
      <ErrorState action={{ label: tCommon('actions.retry'), onClick: () => void refetch() }} />
    );

  if (filteredConversations.length === 0) {
    return (
      <EmptyState
        icon={<MessageSquareMoreIcon size={48} color="gray" />}
        title={t('inbox.empty.line1')}
        description={t('inbox.empty.line2')}
        action={{ label: t('inbox.empty.action'), onClick: () => navigate('/discover') }}
      />
    );
  }

  return (
    <Table.Root>
      <Table.Body>
        {filteredConversations.map(conv => {
          const otherId = conv.userUids.find(id => id !== user?.uid);
          const partner = otherId ? partnerCache[otherId] : undefined;

          return (
            <ConversationRow
              key={conv.id}
              conversation={conv}
              name={partner?.name ?? '…'}
              photoUrl={partner ? firstPhotoUrl(partner.photos) : undefined}
              onClick={() => navigate(`/chat/${conv.id}`)}
            />
          );
        })}
      </Table.Body>
    </Table.Root>
  );
}
